import { useState } from 'react';
import { motion } from 'framer-motion';
import { Monitor, Camera, Cpu, Wifi, Battery, RotateCcw, ChevronRight } from 'lucide-react';
import styled from 'styled-components';

// Styled Components
const Section = styled.section`
  padding: 4rem 1rem;
  background: white;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 4rem 4rem;
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 1.5rem;
  }
  
  p {
    font-size: clamp(1rem, 2vw, 1.25rem);
    color: #6b7280;
    max-width: 48rem;
    margin: 0 auto;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const TabButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 9999px;
  border: 2px solid #e5e7eb;
  background: white;
  color: #6b7280;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    transform: translateY(-2px);
  }
  
  &.active {
    background: #3b82f6;
    border-color: #3b82f6;
    color: white;
    box-shadow: 0 10px 25px rgba(59, 130, 246, 0.3);
  }
  
  .icon {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 1024px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const InfoSection = styled.div`
  h3 {
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #6b7280;
    line-height: 1.6;
    margin-bottom: 2rem;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  .feature-item {
    padding: 1.5rem;
    background: rgba(59, 130, 246, 0.05);
    border-radius: 1rem;
    border: 1px solid rgba(59, 130, 246, 0.1);
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(59, 130, 246, 0.1);
      transform: translateX(0.5rem);
    }
    
    .feature-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.5rem;
      
      .feature-name {
        font-weight: 600;
        color: #1f2937;
        font-size: 1.125rem;
      }
      
      .feature-value {
        font-weight: 700;
        color: #3b82f6;
        font-size: 1.125rem;
      }
    }
    
    .feature-detail {
      color: #6b7280;
      font-size: 0.875rem;
      line-height: 1.5;
    }
  }
`;

const VisualSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  .phone-mockup {
    width: 20rem;
    height: 40rem;
    background: linear-gradient(135deg, #1a1a2e 0%, #0f0f23 100%);
    border-radius: 2.5rem;
    padding: 0.5rem;
    position: relative;
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
    
    .phone-screen {
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, #007AFF 0%, #a78bfa 100%);
      border-radius: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
      
      .screen-content {
        color: white;
        text-align: center;
        z-index: 2;
        position: relative;
        
        h4 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }
        
        .specs-highlight {
          font-size: 2.5rem;
          font-weight: 900;
          margin-bottom: 0.5rem;
        }
        
        .specs-subtitle {
          font-size: 1rem;
          opacity: 0.8;
        }
      }
      
      .animated-bg {
        position: absolute;
        inset: 0;
        background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
        transform: translateX(-100%);
        animation: shimmer 2s infinite;
      }
      
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    }
    
    .phone-notch {
      position: absolute;
      top: 1rem;
      left: 50%;
      transform: translateX(-50%);
      width: 8rem;
      height: 2rem;
      background: #000;
      border-radius: 0 0 1rem 1rem;
    }
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: 4rem;
  
  .cta-box {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(147, 51, 234, 0.1));
    border-radius: 1.5rem;
    padding: 2rem 3rem;
    
    @media (min-width: 768px) {
      padding: 3rem 4rem;
    }
  }
  
  h3 {
    font-size: clamp(1.5rem, 3vw, 2rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    color: #6b7280;
    margin-bottom: 2rem;
  }
  
  .btn-primary {
    background: #007AFF;
    color: white;
    font-weight: 600;
    padding: 0.75rem 2rem;
    border-radius: 9999px;
    transition: all 0.3s ease;
    transform: scale(1);
    box-shadow: 0 10px 25px rgba(0, 122, 255, 0.3);
    border: none;
    cursor: pointer;
    
    &:hover {
      background: #0056CC;
      transform: scale(1.05);
    }
  }
`;

const SpecsSection = () => {
  const [activeTab, setActiveTab] = useState('screen');

  const tabs = [
    { id: 'screen', name: 'Écran', icon: Monitor },
    { id: 'camera', name: 'Appareils photo', icon: Camera },
    { id: 'performance', name: 'Performance', icon: Cpu },
    { id: 'connectivity', name: 'Connectivité', icon: Wifi },
    { id: 'battery', name: 'Batterie', icon: Battery },
  ];

  const specsData = {
    screen: {
      title: 'Écran Super Retina XDR',
      description: 'L\'écran le plus lumineux et le plus coloré jamais créé pour iPhone',
      features: [
        {
          name: 'Technologie',
          value: 'OLED avec ProMotion',
          detail: 'Taux de rafraîchissement adaptatif de 120Hz'
        },
        {
          name: 'Résolution',
          value: '2556 x 1179 pixels',
          detail: '460 ppp - Résolution Retina HD'
        },
        {
          name: 'Luminosité',
          value: 'Jusqu\'à 2000 nits',
          detail: '2x plus lumineux que la génération précédente'
        },
        {
          name: 'Taille',
          value: '6.1 pouces',
          detail: 'Format optimal pour une utilisation à une main'
        },
        {
          name: 'Revêtement',
          value: 'Oléophobe',
          detail: 'Résistant aux empreintes digitales'
        },
        {
          name: 'True Tone',
          value: 'Oui',
          detail: 'Ajuste automatiquement la balance des blancs'
        }
      ],
      image: 'placeholder-screen'
    },
    camera: {
      title: 'Système caméra pro',
      description: 'La photographie professionnelle dans votre poche',
      features: [
        {
          name: 'Caméra principale',
          value: '48 MP',
          detail: 'Capteur grand angle avec pixel binning'
        },
        {
          name: 'Ultra grand-angle',
          value: '12 MP',
          detail: 'Champ de vision 120° avec autofocus'
        },
        {
          name: 'Téléobjectif',
          value: '12 MP',
          detail: 'Zoom optique 5x, zoom numérique jusqu\'à 25x'
        },
        {
          name: 'Ouverture',
          value: 'ƒ/1.78',
          detail: 'Excellent en basse lumière'
        },
        {
          name: 'Mode Pro',
          value: 'ProRAW & ProRes',
          detail: 'Contrôle total sur l\'édition photo et vidéo'
        },
        {
          name: 'Vidéo',
          value: '4K à 60 fps',
          detail: 'Cinematic Mode en 4K, Action Mode'
        }
      ],
      image: 'placeholder-camera'
    },
    performance: {
      title: 'Puce A18 Pro',
      description: 'La puce la plus puissante jamais conçue pour un smartphone',
      features: [
        {
          name: 'Architecture',
          value: '3 nanomètres',
          detail: 'Dernière génération de processus de fabrication'
        },
        {
          name: 'CPU',
          value: '6 cœurs',
          detail: '2 cœurs performance + 4 cœurs efficacité'
        },
        {
          name: 'GPU',
          value: '6 cœurs',
          detail: 'Graphismes console de nouvelle génération'
        },
        {
          name: 'Neural Engine',
          value: '16 cœurs',
          detail: 'Intelligence artificielle et machine learning'
        },
        {
          name: 'RAM',
          value: '8 Go',
          detail: 'Multitâches fluide et applications exigeantes'
        },
        {
          name: 'Gaming',
          value: 'Ray tracing',
          detail: 'Graphismes temps réel ultra-réalistes'
        }
      ],
      image: 'placeholder-performance'
    },
    connectivity: {
      title: 'Connectivité nouvelle génération',
      description: 'Restez connecté avec les technologies les plus avancées',
      features: [
        {
          name: '5G',
          value: 'Ultra-rapide',
          detail: 'Téléchargements jusqu\'à 10 Gbps'
        },
        {
          name: 'Wi-Fi',
          value: 'Wi-Fi 6E',
          detail: 'Connexion plus rapide et plus fiable'
        },
        {
          name: 'Bluetooth',
          value: '5.3',
          detail: 'Meilleure efficacité énergétique'
        },
        {
          name: 'USB-C',
          value: 'USB 3',
          detail: 'Transferts jusqu\'à 20 Gbps'
        },
        {
          name: 'NFC',
          value: 'Oui',
          detail: 'Apple Pay et autres services sans contact'
        },
        {
          name: 'Localisation',
          value: 'GPS, GLONASS',
          detail: 'Précision de localisation améliorée'
        }
      ],
      image: 'placeholder-connectivity'
    },
    battery: {
      title: 'Autonomie exceptionnelle',
      description: 'Jusqu\'à deux jours d\'utilisation avec une seule charge',
      features: [
        {
          name: 'Capacité',
          value: '3274 mAh',
          detail: 'Optimisée par iOS et la puce A18 Pro'
        },
        {
          name: 'Usage vidéo',
          value: '23 heures',
          detail: 'Lecture vidéo en continu'
        },
        {
          name: 'Usage streaming',
          value: '20 heures',
          detail: 'Vidéo streaming en ligne'
        },
        {
          name: 'Charge rapide',
          value: '50% en 30 min',
          detail: 'Avec chargeur 20W ou plus'
        },
        {
          name: 'Charge sans fil',
          value: 'MagSafe & Qi',
          detail: 'Jusqu\'à 15W avec MagSafe'
        },
        {
          name: 'Charge inversée',
          value: 'Oui',
          detail: 'Partage d\'énergie avec accessoires'
        }
      ],
      image: 'placeholder-battery'
    }
  };

  const currentSpecs = specsData[activeTab];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <Section id="specs">
      <Container>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Header>
            <h2>Caractéristiques techniques</h2>
            <p>
              Découvrez les spécifications détaillées qui font de l'iPhone 16 Pro un appareil exceptionnel.
            </p>
          </Header>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <TabsContainer>
            {tabs.map((tab) => (
              <TabButton
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'active' : ''}
              >
                <tab.icon className="icon" />
                {tab.name}
              </TabButton>
            ))}
          </TabsContainer>
        </motion.div>

        {/* Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ContentContainer>
            {/* Info Section */}
            <InfoSection>
              <h3>{currentSpecs.title}</h3>
              <p>{currentSpecs.description}</p>
              
              <FeaturesList>
                {currentSpecs.features.map((feature, index) => (
                  <motion.div
                    key={index}
                    className="feature-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ x: 10 }}
                  >
                    <div className="feature-header">
                      <span className="feature-name">{feature.name}</span>
                      <span className="feature-value">{feature.value}</span>
                    </div>
                    <p className="feature-detail">{feature.detail}</p>
                  </motion.div>
                ))}
              </FeaturesList>
            </InfoSection>

            {/* Visual Section */}
            <VisualSection>
              <motion.div
                className="phone-mockup"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                whileHover={{ rotate: 5 }}
              >
                <div className="phone-screen">
                  <div className="screen-content">
                    <h4>{currentSpecs.title}</h4>
                    <div className="specs-highlight">
                      {currentSpecs.features[0]?.value}
                    </div>
                    <p className="specs-subtitle">
                      {currentSpecs.features[0]?.name}
                    </p>
                  </div>
                  <div className="animated-bg"></div>
                </div>
                <div className="phone-notch"></div>
              </motion.div>
            </VisualSection>
          </ContentContainer>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <CTASection>
            <div className="cta-box">
              <h3>Explorez toutes les fonctionnalités</h3>
              <p>
                Découvrez comment chaque caractéristique améliore votre expérience quotidienne.
              </p>
              <button className="btn-primary">
                Voir le guide complet
              </button>
            </div>
          </CTASection>
        </motion.div>
      </Container>
    </Section>
  );
};

export default SpecsSection;
